import { apiClient } from "@/client";

const INVALID_MESSAGE =
	"Please enter a valid phone number, e.g. (559) 123-4567.";

interface ValidatePhoneNumberResponse {
	valid: boolean;
	e164?: string;
}

/**
 * Validates a phone number as a valid E164 number by asking the backend at
 * GET /api/validate/phone-number, which is the authoritative source (it runs
 * the same `text.ParsePhoneNumber` US-default check the reporter submit paths
 * use). Returns an error message string when the number cannot be parsed into
 * a valid E164, or `null` when it can. Blank input is considered valid, since
 * these fields are optional on the reporter forms.
 *
 * On a network error we fail open (return `null`): the backend re-validates
 * every number at submit time, so a transient outage here must not block a
 * reporter from submitting a good form.
 */
export async function phoneValidationError(
	input: string,
): Promise<string | null> {
	const trimmed = (input ?? "").trim();
	if (trimmed === "") {
		return null;
	}
	try {
		const resp = await apiClient.JSONGet<ValidatePhoneNumberResponse>(
			"/api/validate/phone-number",
			{ params: { n: trimmed } },
		);
		return resp.valid ? null : INVALID_MESSAGE;
	} catch {
		return null;
	}
}
